import { useLazyQuery } from '@apollo/client'
import capitalize from 'lodash-es/capitalize'
// TODO: Fix this auto-generated suppression.
// eslint-disable-next-line rulesdir/require-next-link-wrapper
import Link from 'next/link'
import { usePathname, useSearchParams } from 'next/navigation'
import { StringifiableRecord } from 'query-string'
import { useCallback, useEffect, useMemo, useRef, useState } from 'react'
import { ReactElement, memo } from 'react'

import {
  ColorSelector,
  MediaAsContents,
  ResponsiveImageWithSkeletonWrapper,
  ViewportSizes,
  useIsBrowser,
  useIsViewport,
  useSupportsHover,
} from '@syconium/little-miss-figgy'

import {
  GetTileProductGroupForCollectionQueryVariables,
  GetTileProductGroupForSearchQueryVariables,
} from '../../../../../../../../__generated__/graphql/catalog/graphql'
import { FloatingFavoritesButton } from '../../../../../../../../app/_components/buttons/FavoritesButton.client'
import { reportClientError } from '../../../../../../../../app/_components/chrome/scripts/DataDogRumScript'
import { usePortal } from '../../../../../../../../app/_providers/PortalProvider.client'
import { useSiteWideMessaging } from '../../../../../../../../app/_providers/SiteWideMessagingProvider.client'
import { useTrackProductInteraction } from '../../../../../../../../app/_providers/TrackingProvider.client'
import { useTranslation } from '../../../../../../../../app/_providers/TranslationProvider.client'
import { Fit, nullSizeChart } from '../../../../../../../../brunswick/constants'
import { Category } from '../../../../../../../../brunswick/constants/category'
import { useCollectionFilterQueryParamsContext } from '../../../../../../../../brunswick/containers/CollectionFilterQueryParamsContext'
import { useHeapAttributes } from '../../../../../../../../containers/HeapAttributesContainer'
import { IEventCustomAttributes, trackEvent } from '../../../../../../../../lib/analytics'
import { GET_TILE_PRODUCT_GROUP_FOR_SEARCH } from '../../../../../../../../lib/graphql/queries'
import { GET_TILE_PRODUCT_GROUP_FOR_COLLECTION } from '../../../../../../../../lib/graphql/queries/GET_TILE_PRODUCT_GROUP_FOR_COLLECTION'
import { useFetchFeaturedProductData } from '../../../../../../../../lib/hooks/useFetchFeaturedProductData/useFetchFeaturedProductData'
import { useGoogleTagManager } from '../../../../../../../../lib/hooks/useGoogleTagManager'
import { useInputObservingState } from '../../../../../../../../lib/hooks/useInputObservingState'
import { useStackedColorsAndSizes } from '../../../../../../../../lib/hooks/useStackedColorsAndSizes'
import { useKitHref, useProductHref } from '../../../../../../../../lib/routing'
import {
  areFitsEquivalent,
  deriveAvailableProducts,
  deriveColorsWithUnavailable,
  deriveFitNamesMap,
  deriveSizes,
  formatPrettyColorName,
  sortColorsByAvailability,
} from '../../../../../../../../lib/utils'
import { QuickBuyContainer } from '../../../../../../../containers/QuickBuyContainer'
import { PriceDisplay } from '../../../../../../PriceDisplay'
import { ColumnLayoutService } from '../../containers/ColumnLayoutService'
import { TileProduct } from '../types'

import { QuickBuyOverlay } from './QuickBuyOverlay'
import {
  Badge,
  Body,
  Color,
  ColorLine,
  ColorSelectorWrapper,
  DetailsContents,
  FitLine,
  ImageContainer,
  ImageContents,
  PriceDisplayWrapper,
  PrimaryImageWrapper,
  ProductName,
  SecondaryImageWrapper,
  SelectColorsOnSaleBadge,
  ShowMore,
  ShowMoreWrap,
  SiteWidePromoWrap,
  StyledIconBadge,
  UnavailableOverlay,
} from './styles'

import type {
  EmbroideryConfig,
  PriceRange,
  ProductColor,
  ProductGroupWithProductAvailability,
  Variant,
} from '../../../../../../../../types/graphql'

export interface ProductGroupTileProps {
  tileProduct: TileProduct
  index: number
}

const MAX_PREVIEW_COLORS = 4

export const ProductGroupTile = memo(
  ({ tileProduct, index }: ProductGroupTileProps): ReactElement => {
    const isCriticalImage = index < 4
    const {
      isKit,
      category,
      rawCategory,
      colorInfo: color,
      defaultVariant,
      productGroup,
      images: imagesList,
      collectionScope,
    } = tileProduct
    const fitKeys = productGroup?.rawFits
    const images = imagesList?.nodes
    const product = tileProduct

    const { numberOfColumnsOnMobile, numberOfColumnsOnDesktop } = ColumnLayoutService.useContainer()

    const analyticsContext = useHeapAttributes()
    const { pushProductClick } = useGoogleTagManager()
    const { trackProductInteraction } = useTrackProductInteraction()
    const productGroupHandle = productGroup.handle
    const [availableFitsKeys, setAvailableFitsKeys] = useState<string[]>(
      product?.rawAvailableFits ?? []
    )
    const supportsHover = useSupportsHover()
    const isMediumOrLargeViewport = useIsViewport([ViewportSizes.medium, ViewportSizes.large])
    const isBrowser = useIsBrowser({ ssr: true })
    const isSmallViewport = !isMediumOrLargeViewport
    const pgDiscountPriceRange = productGroup?.priceRange // this field was reused in the new optimized API to return the discount price range for the product group during BFCM
    // Only display quick buy CTA for shoes overlay, or for smaller viewports

    const { siteWidePromotionMessage: siteWidePromo } = useSiteWideMessaging()
    const {
      collections: { productGroupTile: strings },
      quickBuy: { soldOutOverlayMessage, waitlistOverlayMessage },
    } = useTranslation()

    const { openModal } = QuickBuyContainer.useContainer()

    const pathname = usePathname()

    const { createEmbroideryConfig, hasMandatoryTextEmbroidery } = usePortal()
    const isPortal = pathname?.includes('/portals/')

    // Don't display quick buy overlay if the tile product enforces mandatory text embroidery for the users portal,
    // or if the `$removeQuickBuy` observable is truthy.
    const disableQuickBuyOverlay = useMemo(() => {
      if (isPortal && tileProduct?.rawCategory) {
        const mandatoryTextEmbroidery = hasMandatoryTextEmbroidery({
          categoryAnalyticsId: tileProduct.rawCategory,
        })
        if (mandatoryTextEmbroidery) return true
      }
      return false
    }, [isPortal, hasMandatoryTextEmbroidery, tileProduct.rawCategory])

    const [interacting, setIsInteracting] = useState(false)

    // TODO: We shouldn't be using fake types for our queries. Remove the generics to allow the real types to flow through.
    const [
      fetchCollectionTile,
      { data: collectionTileData, error: collectionTileError, loading: collectionTileLoading },
    ] = useLazyQuery<
      {
        collection?: { productGroup: ProductGroupWithProductAvailability }
      },
      GetTileProductGroupForCollectionQueryVariables
    >(GET_TILE_PRODUCT_GROUP_FOR_COLLECTION, {
      ssr: false,
      variables: {
        // productGroupHandle must be the first variable to support suffix wildcard match:
        productGroupHandle,
        color: color?.name,
        collectionHandle: tileProduct.collectionScope?.collectionHandle ?? '',
        collectionColorScope: tileProduct.collectionScope?.collectionColorScope ?? 'ALL',
        withEmbroideryOptions: isPortal,
        withEmbroideryScope: isPortal ? 'PORTALS' : 'DTC',
      },
    })

    const [fetchSearchTile, { data: searchTileData, error: searchTileError }] = useLazyQuery<
      {
        productGroup: ProductGroupWithProductAvailability | null
      },
      GetTileProductGroupForSearchQueryVariables
    >(GET_TILE_PRODUCT_GROUP_FOR_SEARCH, {
      variables: {
        color: color?.name,
        handle: productGroupHandle,
      },
    })

    const error = collectionTileError ?? searchTileError
    const tileProductGroup = collectionTileData
      ? collectionTileData?.collection?.productGroup
      : searchTileData?.productGroup

    // Derive the embroidery config for the current product tile
    // Only applicable for portal users which have available logo embroidery settings.
    const portalEmbroideryConfig = useMemo<EmbroideryConfig | undefined>(() => {
      const logoOnlyEmbroideryOption = tileProductGroup?.featuredProduct?.embroidery?.find(
        ({ kind }) => kind === 'LOGO_ONLY'
      )
      const logoOnlyEmbroideryVariant = logoOnlyEmbroideryOption?.variants[0]
      if (!logoOnlyEmbroideryVariant) return undefined

      return createEmbroideryConfig({
        embroideryVariant: logoOnlyEmbroideryVariant,
        categoryAnalyticsId: product.rawCategory,
      })
    }, [tileProductGroup, createEmbroideryConfig, product.rawCategory])

    // Conditionally request the tile product from within the context of a collection
    // if present, or fetch the product group query directly if not (i.e. for search)
    const fetchTileProduct = useCallback(async () => {
      if (tileProduct.collectionScope) {
        await fetchCollectionTile()
      } else {
        await fetchSearchTile()
      }
    }, [fetchCollectionTile, fetchSearchTile, tileProduct.collectionScope])

    useEffect(() => {
      if (interacting) fetchTileProduct()
    }, [fetchTileProduct, interacting])

    const availableProducts = useMemo(() => {
      if (!productGroup?.productsForAvailability) return {}
      return deriveAvailableProducts(productGroup?.productsForAvailability)
    }, [productGroup?.productsForAvailability])
    const colors = tileProduct.productGroup.colors

    const { state: selectedColor, setState: setSelectedColor } = useInputObservingState<
      ProductColor | undefined
    >(color)

    const { state: selectedFitKey, setState: setSelectedFitKey } = useInputObservingState<
      string | undefined
    >(undefined)

    const selectedColorName = selectedColor?.name
    const tileProductGroupFeaturedProduct = tileProductGroup?.featuredProduct
    const useInitialFeaturedProduct =
      tileProductGroupFeaturedProduct?.color === selectedColorName &&
      areFitsEquivalent(tileProductGroupFeaturedProduct?.rawFit ?? '', selectedFitKey ?? '')

    const { featuredProduct: fetchedFeaturedProduct } = useFetchFeaturedProductData({
      color: selectedColorName,
      fit: selectedFitKey || Fit.REGULAR,
      productGroupHandle: productGroupHandle,
      skip: !tileProductGroup || useInitialFeaturedProduct,
    })

    const featuredProduct = useInitialFeaturedProduct
      ? tileProductGroupFeaturedProduct
      : fetchedFeaturedProduct ?? tileProductGroupFeaturedProduct

    const { variants: featuredProductVariants = [] as Variant[] } = featuredProduct ?? {}

    const colorKey = selectedColor?.rawName

    const [collectionBadgeColor, collectionBadgeText, swPromoEligible] = useMemo(() => {
      const isMatch = product?.colorInfo?.rawName === colorKey
      const source = isMatch ? product : featuredProduct

      if (!collectionTileLoading) {
        setAvailableFitsKeys(source?.rawAvailableFits ?? [])
      }

      return [source?.collectionBadgeColor, source?.collectionBadgeText, source?.swPromoEligible]
    }, [colorKey, collectionTileLoading, featuredProduct, product])

    const colorsWithUnavailable = sortColorsByAvailability(
      deriveColorsWithUnavailable({
        availableProducts,
        colors: productGroup?.colors ?? [],
        selectedFitKey: featuredProduct?.rawFit ?? Fit.REGULAR,
      })
    )

    const previewColors = colorsWithUnavailable
      .sort((a, b) => {
        const getPriority = (swatch: ProductColor & { unavailable: boolean }) => {
          if (swatch.rawName === color?.rawName) return 0 // Default color first
          if (swatch.unavailable === false) return 1 // Available second
          if (swatch.availability === 'core') return 2 // Core colors third
          return 3 // All others (e.g., limited edition)
        }

        return getPriority(a) - getPriority(b)
      })
      .slice(0, MAX_PREVIEW_COLORS)
    const colorsLength = colorsWithUnavailable?.length || 0
    const previewColorIndex = previewColors.findIndex(
      item => item?.hexCodes.includes(selectedColor?.hexCodes[0] || '')
    )

    const productGroupSizes = productGroup?.sizes ?? tileProductGroup?.sizes ?? []
    const sizes = deriveSizes(featuredProductVariants, productGroupSizes)
    const fitNames = useMemo(() => deriveFitNamesMap(productGroup), [productGroup])

    const fitsAvailabilityEndingText: string | undefined =
      availableFitsKeys &&
      availableFitsKeys
        .filter(fitKey => fitKey !== Fit.REGULAR)
        .map(fitKey => capitalize(fitNames?.get(fitKey) ?? fitKey))
        .join(' & ')

    const fitsAvailabilityText: string | null =
      fitsAvailabilityEndingText && !isKit
        ? `${strings.fitsAvailabilityBeginningText} ${fitsAvailabilityEndingText}`
        : null

    const activeVariant = featuredProduct?.variants?.[0] ?? defaultVariant

    const currency = activeVariant?.priceDetails?.price?.currency
    const price = activeVariant?.priceDetails?.price?.amount
    const discountPrice = activeVariant?.priceDetails?.discountPrice?.amount

    const isCurrentColorCompletelySoldOut = availableFitsKeys
      ? availableFitsKeys.length <= 0 && !isKit
      : false

    const priceRange: PriceRange | undefined = product?.priceRange

    const tileImages: { source: string }[] | undefined = useMemo(() => {
      return featuredProduct?.images?.nodes ?? images
    }, [featuredProduct, images])

    const sizeChart =
      tileProductGroup?.sizeChart?.name === nullSizeChart ? undefined : tileProductGroup?.sizeChart

    const openQuickBuyModal = () => {
      setIsInteracting(true)
    }

    // TODO: Refactor this, a useEffect is an inappropriate way for opening a modal.
    useEffect(() => {
      if (!selectedColor || !tileProductGroup || isKit || interacting === false) return
      const mergedProductData = { ...tileProductGroup, colors: colors }

      if (featuredProduct) {
        openModal({
          analyticsContext,
          availableProducts,
          embroideryConfig: portalEmbroideryConfig,
          initialFeaturedProduct: featuredProduct,
          initialSelectedColor: selectedColor,
          productGroup: mergedProductData,
          sizeChart,
          swPromoEligible,
          variants: featuredProductVariants,
        })
      }
      setIsInteracting(false)
      // TODO: Fix this auto-generated suppression.
      // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [
      analyticsContext,
      colors,
      featuredProductVariants,
      interacting,
      openModal,
      selectedColor,
      sizeChart,
      supportsHover,
      tileProductGroup,
    ])

    useEffect(() => {
      if (!error || !productGroupHandle) return
      if (error?.message === 'Search returned no results') return
      reportClientError({
        error,
        context: {
          scope: 'ProductGroupTile query',
          productGroupHandle,
          label: 'collection query failed',
        },
      })
    }, [error, productGroupHandle])

    const { title } = productGroup

    const featuredProductWaitlistable = featuredProduct?.waitlistable ?? product.waitlistable
    const activeProductFinalSale = product?.finalSale
    const coloredVariants = featuredProductVariants
    const discountPriceExplicitFmt = activeVariant?.discountPriceExplicitFmt
    const iconBadge = product?.iconBadge ?? productGroup.iconBadge
    const iconBadgePlacement = product?.iconBadgePlacement ?? productGroup.iconBadgePlacement
    const imageSource = tileImages && tileImages[0]?.source
    const priceExplicitFmt = activeVariant?.priceExplicitFmt
    const secondaryImageSource = tileImages && tileImages[1]?.source
    const unavailableOverlayMessage = featuredProductWaitlistable
      ? waitlistOverlayMessage
      : soldOutOverlayMessage
    const customHeapAttributes = useMemo((): IEventCustomAttributes => {
      return {
        'data-heap-collection-badge-label': collectionBadgeText,
        'data-heap-collection-badge': collectionBadgeText ? 'true' : 'false',
        'data-heap-position': `${index + 1}`,
      }
    }, [collectionBadgeText, index])
    const { selectedFilters } = useCollectionFilterQueryParamsContext()
    // if a specific color is selected on the productGroup or through collection filters we want to show the price of the selected color not the range
    const [hasColorChanged, setHasColorChanged] = useState(false)
    const bodyRef = useRef<HTMLDivElement>(null)
    const imageContainerRef = useRef<HTMLDivElement>(null)
    const { useLargeWidthSizeSelectorItems } = useStackedColorsAndSizes(rawCategory)
    const displayedFitKey =
      selectedFitKey ??
      tileProductGroupFeaturedProduct?.rawFit ??
      tileProduct.rawFit ??
      tileProduct.defaultVariant?.rawFit
    const displayedColorKey =
      selectedColor?.rawName ??
      tileProductGroupFeaturedProduct?.colorInfo?.rawName ??
      tileProduct.defaultVariant?.rawColor

    // if translated name does not exist use default english name
    const colorDisplayName = selectedColor?.name ?? selectedColor?.rawName

    // Whether the quick buy overlay should only show the CTA or the entire variant selector(s)
    const overlayCtaOnly = rawCategory.toLowerCase() === 'shoes' || isSmallViewport

    const hasColorFilter = useMemo(
      () => (selectedFilters.hasOwnProperty('color') ? true : false),
      [selectedFilters]
    )

    // plain=true query param is used by iOS app to view webpages within a webview without the website's header/footer
    const plainQueryParamValue = useSearchParams()?.get('plain') ?? undefined

    const query: StringifiableRecord = {
      color: colorKey,
      plain: plainQueryParamValue,
    }

    const kitHref = useKitHref(productGroupHandle, query)
    const productHref = useProductHref(
      {
        productGroupHandle: productGroupHandle,
        exclusive: product.exclusive ?? false,
        genderCategory: productGroup.genderCategory,
      },
      query
    )
    const pdpLinkPath = isKit ? kitHref : productHref

    const onProductLinkClicked = () => {
      trackProductInteraction({
        handle: productGroupHandle,
        color: colorKey ?? '',
        id: featuredProduct ? featuredProduct.id : product.id,
        shopifyId: featuredProduct ? featuredProduct.shopifyId : product.shopifyId,
      })
      if (!defaultVariant) return
      pushProductClick({
        variant: defaultVariant,
        list: analyticsContext.list,
        position: index,
      })
    }

    const onColorSelected = async (selectedColor: ProductColor) => {
      await fetchTileProduct()
      setSelectedColor(selectedColor)
      setHasColorChanged(true)
    }
    const showDefaultProductPrice = collectionScope?.pricingDisplay === 'defaultProductPrice'
    const displaySelectColorsSaleBadge =
      pgDiscountPriceRange &&
      pgDiscountPriceRange.max !== pgDiscountPriceRange.min &&
      showDefaultProductPrice
    return (
      <Body
        data-availability={isCurrentColorCompletelySoldOut ? 'unavailable' : 'available'}
        ref={bodyRef}
      >
        <ImageContents>
          <ImageContainer ref={imageContainerRef}>
            <Link href={pdpLinkPath} onClick={onProductLinkClicked}>
              {imageSource ? (
                <PrimaryImageWrapper>
                  <ResponsiveImageWithSkeletonWrapper
                    data-testid={`product-group-tile-image--${productGroupHandle}`}
                    {...trackEvent({
                      action: 'click',
                      category: analyticsContext.category,
                      customAttributes: customHeapAttributes,
                      label: 'product image',
                      value: `${productGroupHandle}-${colorKey?.toLocaleLowerCase()}`,
                    })}
                    alt=''
                    loading={isCriticalImage ? 'default' : 'lazy'}
                    fetchPriority={isCriticalImage ? 'high' : undefined}
                    src={imageSource}
                    aspectRatios={{
                      sm: 20 / 26,
                      md: 20 / 26,
                    }}
                    widths={{
                      unit: 'vw',
                      sm: 100 / numberOfColumnsOnMobile,
                      md: 100 / numberOfColumnsOnDesktop,
                    }}
                  />
                </PrimaryImageWrapper>
              ) : null}
              {secondaryImageSource && (
                <SecondaryImageWrapper>
                  <ResponsiveImageWithSkeletonWrapper
                    alt=''
                    loading='lazy'
                    fetchPriority='low'
                    src={secondaryImageSource}
                    {...trackEvent({
                      action: 'click',
                      category: analyticsContext.category,
                      customAttributes: customHeapAttributes,
                      label: 'product image',
                      value: `${productGroupHandle}-${colorKey?.toLocaleLowerCase()}`,
                    })}
                    aspectRatios={{
                      sm: 20 / 26,
                      md: 20 / 26,
                    }}
                    widths={{
                      unit: 'vw',
                      sm: 100 / numberOfColumnsOnMobile,
                      md: 100 / numberOfColumnsOnDesktop,
                    }}
                  />
                </SecondaryImageWrapper>
              )}
              {iconBadge && isBrowser ? (
                <>
                  <MediaAsContents lessThan='md'>
                    <StyledIconBadge
                      caption={iconBadge.caption}
                      captionColor={iconBadge.captionColor}
                      icon={iconBadge.icon}
                      iconText={iconBadge.iconText}
                      iconBadgePlacement={iconBadgePlacement}
                      size={'small'}
                    />
                  </MediaAsContents>
                  <MediaAsContents greaterThanOrEqual='md'>
                    <StyledIconBadge
                      caption={iconBadge.caption}
                      captionColor={iconBadge.captionColor}
                      icon={iconBadge.icon}
                      iconText={iconBadge.iconText}
                      iconBadgePlacement={iconBadgePlacement}
                      size={'large'}
                    />
                  </MediaAsContents>
                </>
              ) : null}
              <UnavailableOverlay
                show={isCurrentColorCompletelySoldOut}
                aria-hidden={isCurrentColorCompletelySoldOut ? 'false' : 'true'}
              >
                {unavailableOverlayMessage}
              </UnavailableOverlay>
            </Link>
            {displayedColorKey && displayedFitKey && !isKit && (
              <FloatingFavoritesButton
                productGroupHandle={productGroupHandle}
                colorKey={displayedColorKey}
                fitKey={displayedFitKey}
                sizeKey={null}
                productGroupImageSrc={imageSource}
              />
            )}
            {!disableQuickBuyOverlay && !error && !isKit && (
              <QuickBuyOverlay
                embroideryConfig={portalEmbroideryConfig}
                activeProductFinalSale={activeProductFinalSale}
                analyticsContext={analyticsContext}
                category={category}
                coloredVariants={coloredVariants}
                isCurrentColorCompletelySoldOut={isCurrentColorCompletelySoldOut}
                openQuickBuyModal={openQuickBuyModal}
                selectedColorKey={selectedColor?.rawName}
                selectedFitKey={selectedFitKey}
                setSelectedFitKey={setSelectedFitKey}
                sizes={sizes}
                swPromoEligible={swPromoEligible}
                useLargeWidthSizeSelectorItems={useLargeWidthSizeSelectorItems}
                rawCategory={rawCategory}
                allFitsKeys={fitKeys}
                attributeSelectorContext={title}
                ctaOnly={overlayCtaOnly}
                fitKeys={availableFitsKeys}
                fitNames={fitNames}
              />
            )}
          </ImageContainer>
          {collectionBadgeText && (
            <Badge colorOverride={collectionBadgeColor} textTransform='uppercase'>
              {collectionBadgeText}
            </Badge>
          )}
          <Link
            title={title}
            href={pdpLinkPath}
            onClick={onProductLinkClicked}
            {...trackEvent({
              action: 'click',
              category: analyticsContext.category,
              customAttributes: customHeapAttributes,
              label: 'product name',
              value: pdpLinkPath.toString(),
            })}
          >
            <ProductName>{title}</ProductName>
          </Link>
        </ImageContents>
        <DetailsContents>
          {(() => {
            const colorLine = (
              <ColorLine key='colorLine'>
                {colorDisplayName && !isKit && (
                  <Color>{formatPrettyColorName(colorDisplayName)}</Color>
                )}
              </ColorLine>
            )

            const promoDisplay = displaySelectColorsSaleBadge ? (
              <SelectColorsOnSaleBadge key='selectColorsOnSaleBadge'>
                {strings.selectColorsOnSale}
              </SelectColorsOnSaleBadge>
            ) : (
              siteWidePromo &&
              swPromoEligible && (
                <SiteWidePromoWrap key='siteWidePromo'>{siteWidePromo}</SiteWidePromoWrap>
              )
            )

            const priceDisplay = (
              <PriceDisplayWrapper key='priceDisplay'>
                <PriceDisplay
                  {...{
                    discountPriceExplicitFmt,
                    priceRange,
                    price,
                    priceExplicitFmt,
                    discountPrice,
                    currency,
                    pgDiscountPriceRange:
                      hasColorChanged || hasColorFilter || showDefaultProductPrice
                        ? undefined
                        : pgDiscountPriceRange,
                  }}
                />
              </PriceDisplayWrapper>
            )
            return [priceDisplay, promoDisplay, colorLine]
          })()}

          {!isKit && (
            <ColorSelectorWrapper>
              <ColorSelector
                colors={previewColors}
                onSelectColor={color => onColorSelected(color)}
                selectedColorKey={selectedColor?.rawName}
                disableMobileHorizontalSwipe
                swatchSpacing='small'
                {...trackEvent({
                  category: analyticsContext.category,
                  action: 'click',
                  label: 'color swatch',
                  value: selectedColor?.rawName,
                  customAttributes: {
                    'data-heap-position': `${previewColorIndex}`,
                    'data-heap-product-handle': productGroupHandle,
                  },
                })}
              />
              {colorsLength > MAX_PREVIEW_COLORS && (
                <ShowMoreWrap>
                  <Link
                    href={pdpLinkPath}
                    onClick={onProductLinkClicked}
                    style={{ display: 'inline-block' }}
                  >
                    <ShowMore>
                      {strings.showMoreText.replace(
                        '{{colorAmount}}',
                        (colorsLength - MAX_PREVIEW_COLORS).toString()
                      )}
                    </ShowMore>
                  </Link>
                </ShowMoreWrap>
              )}
            </ColorSelectorWrapper>
          )}
          {fitsAvailabilityText && category !== Category.ScrubPants && (
            <FitLine>{fitsAvailabilityText}</FitLine>
          )}
        </DetailsContents>
      </Body>
    )
  }
)
